/**
 * VisitReceiptsModal — Phase 12.5
 *
 * Two-tab dialog opened from the 🖨️ button on a visit row:
 *
 *   ┌ Receipts ┬ Refund ┐
 *   │  list of all canonical Payments on the visit. Checkbox per
 *   │  non-refund row + "Print all"; clicking Print → opens single
 *   │  receipt or bundled PDF in the FileViewerStore.
 *   │  Inline "Reprint queue token" link preserves the existing 80mm
 *   │  thermal flow for clinics that want it.
 *   │
 *   │  For each refundable row: amount input (clamped to remaining),
 *   │  reason dropdown, method dropdown (defaults to parent's method),
 *   │  Submit. Running total below; confirm before commit. Success
 *   │  state shows "Print refund slip" CTA.
 *   └─────────────────────┘
 *
 * All backend endpoints live in receiptsApi.js + patientPlanApi.js.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listReceiptsForVisit,
  refundPayment,
  openRefundSlipPdf,
  sumCollectedRupees,
  shouldItemizeToken,
  tokenLineItemsFromReceipts,
  refundLinesFromReceipts,
  netPaid,
} from '../../services/api/receiptsApi';
import { newIdempotencyKey } from '../../services/api/admissionPaymentsApi';
import { openCombinedReceiptPdf } from '../../services/api/patientPlanApi';
import { printToken } from '../../utils/printToken';
import { tokenProviderFields } from './advisedByAttribution';
import { Z_DIALOG } from '../../constants/zLayers';


// Common refund reasons surfaced as quick picks; "Other" reveals a
// free-text field. Slugs are submitted to the backend.
const REFUND_REASONS = [
  { value: 'doctor_unavailable', label: 'Doctor not available' },
  { value: 'patient_left', label: 'Patient left' },
  { value: 'procedure_not_done', label: 'Procedure not done' },
  { value: 'service_issue', label: 'Service issue' },
  { value: 'discount', label: 'Discount' },
  { value: 'other', label: 'Other…' },
];

const REFUND_METHODS = ['CASH', 'UPI', 'CARD', 'NETBANKING', 'WALLET'];


function fmtMoney(value) {
  if (value === null || value === undefined) return '0.00';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  // Refund net is negative — show the absolute value with a minus prefix.
  const sign = num < 0 ? '-' : '';
  return `${sign}${Math.abs(num).toFixed(2)}`;
}


/**
 * Three compact tiles at the top of the Payments tab — Collected /
 * Refunded / Net for this visit. Palette matches DailyCollectionSummary
 * (solid backgrounds, ≥-400 borders) so the visual language stays
 * consistent between the two surfaces.
 */
function PaymentsSummaryTiles({ collected, refunded, net, isDark }) {
  const tile = (label, value, variant) => {
    const palette = (() => {
      if (variant === 'headline') {
        return isDark
          ? { bg: '#134e4a', border: 'rgba(94,234,212,0.45)', label: '#5eead4', value: '#ffffff' }
          : { bg: '#ccfbf1', border: '#14b8a6',                label: '#0f766e', value: '#0f172a' };
      }
      if (variant === 'refund') {
        return isDark
          ? { bg: '#3b0a0a', border: 'rgba(239,68,68,0.5)',    label: '#fca5a5', value: '#fecaca' }
          : { bg: '#fef2f2', border: '#fca5a5',                 label: '#b91c1c', value: '#7f1d1d' };
      }
      return isDark
        ? { bg: '#0f172a',   border: 'rgba(255,255,255,0.12)', label: '#94a3b8', value: '#e2e8f0' }
        : { bg: '#ffffff',   border: '#cbd5e1',                label: '#475569', value: '#0f172a' };
    })();
    return (
      <div
        key={label}
        style={{
          flex: variant === 'headline' ? '1.4 1 0' : '1 1 0',
          minWidth: '110px',
          background: palette.bg,
          border: `1px solid ${palette.border}`,
          borderRadius: '0.5rem',
          padding: '0.5rem 0.625rem',
          display: 'flex', flexDirection: 'column', gap: '0.15rem',
        }}
      >
        <div style={{
          fontSize: '0.65rem', fontWeight: 600,
          letterSpacing: '0.02em', textTransform: 'uppercase',
          color: palette.label,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: variant === 'headline' ? '1.05rem' : '0.95rem',
          fontWeight: variant === 'headline' ? 700 : 600,
          color: palette.value, lineHeight: 1.2,
        }}>
          {value}
        </div>
      </div>
    );
  };
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.5rem',
      marginBottom: '0.75rem',
    }}>
      {tile('Collected', `₹${fmtMoney(collected)}`)}
      {tile('Refunded', `₹${fmtMoney(refunded)}`, 'refund')}
      {tile('Net for visit', `₹${fmtMoney(net)}`, 'headline')}
    </div>
  );
}


// W7 — refund rows carry `receipt_series: "CN"` for a new credit-note
// refund (money series stays undisambiguated, "#N" as before). Single
// source for the two refund-history audit strips below so they can't drift.
// Copy "CN N" is PROVISIONAL (no dev free-hand microcopy — PO/Business to
// ratify the final credit-note display format).
function refundReceiptLabel(row) {
  const num = row?.receipt_number ?? '—';
  return row?.receipt_series === 'CN' ? `CN ${num}` : `#${num}`;
}

// F18 — single source for the auto-cancel warning copy so the row hint
// and the confirm dialog (which overlays the row while open) can never
// say different things. `autoCancelOn` is the row's
// `auto_cancel_on_refund` value ('visit' | 'sitting'); wording unchanged
// from the pre-F18 static hint.
function autoCancelWarningText(autoCancelOn) {
  const noun = autoCancelOn === 'visit' ? 'visit' : 'procedure';
  const queueLabel = autoCancelOn === 'visit' ? 'consultation' : 'procedure';
  return `⚠ Refunding will cancel this ${noun} and remove it from the ${queueLabel} queue.`;
}

function badge(type) {
  switch (type) {
    case 'REFUND':       return { icon: '↩️', label: 'Refund' };
    case 'CONSULTATION': return { icon: '🩺', label: 'Consult' };
    case 'REGISTRATION': return { icon: '📋', label: 'Registration' };
    case 'REFERRAL_HOP': return { icon: '➡️', label: 'Referral' };
    case 'PROCEDURE':    return { icon: '🦷', label: 'Procedure' };
    case 'UPFRONT_PLAN': return { icon: '💼', label: 'Upfront' };
    default:             return { icon: '•', label: type || '—' };
  }
}


export default function VisitReceiptsModal({
  visitId,
  visit,
  isOpen,
  onClose,
  isDark = false,
  branding = {},
  // 'receipts' = printing surface (default), 'refund' = refund surface.
  // When `mode` is set, the modal hides the tab bar — refund and
  // receipts are reached via separate buttons on the visit row.
  mode = 'receipts',
  // F2 — the visit's department-of-care display name, resolved by the
  // CALLER (ManageVisitPage, from the departmentStore it already has
  // loaded — single source, no fetch in this modal). undefined/null when
  // the visit has no department or the dept isn't in the caller's cache;
  // the reprinted token simply omits the row in that case (documented
  // gap — never worth a fetch just for a reprint token row).
  departmentName,
}) {
  const [activeTab, setActiveTab] = useState(mode);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Receipts-tab selection
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [printing, setPrinting] = useState(false);

  // Refund-tab per-row form state, keyed by payment_id
  const [refundForms, setRefundForms] = useState({});
  const [submittingRefundId, setSubmittingRefundId] = useState(null);
  const [refundError, setRefundError] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // {paymentId, …}
  const [lastRefund, setLastRefund] = useState(null);          // success-state card

  // FE-4 (#11) — an IPD-lab row's `visitId` is a `lab_`-prefixed lab_order_id
  // (not a VISIT id). Bound to the FROZEN contract: a settled order resolves
  // one receipt; an UNSETTLED order returns `200 {receipts: []}` (machine-
  // readable empty-state, never a 404 "not found"). Detected client-side so
  // the empty-state copy can differ from the generic "no receipts" message
  // without a second fetch or a new endpoint.
  const isLabOrderId = typeof visitId === 'string' && visitId.startsWith('lab_');

  // ── Fetch on open ──────────────────────────────────────────────
  const fetchReceipts = useCallback(async () => {
    if (!visitId) return;
    setLoading(true);
    setError(null);
    const res = await listReceiptsForVisit(visitId);
    if (res.success) {
      setReceipts(res.data);
    } else {
      setError(res.error || 'Could not load receipts.');
      setReceipts([]);
    }
    setLoading(false);
  }, [visitId]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(mode);
    setSelectedIds(new Set());
    setRefundForms({});
    setRefundError(null);
    setPendingConfirm(null);
    setLastRefund(null);
    fetchReceipts();
  }, [isOpen, mode, fetchReceipts]);

  // ── Derived data ───────────────────────────────────────────────
  const printableReceipts = useMemo(
    () => receipts.filter((r) => r.source_type !== 'REFUND'),
    [receipts],
  );
  // Payments tab — the full list of non-REFUND, non-VOIDED rows
  // including ones with zero refundable balance, so the staff can
  // see the entire money state of the visit (not just what's still
  // refundable). Refund forms render only on the rows that still
  // have a positive `refundable_remaining_rupees`.
  const paymentRows = useMemo(
    () => receipts.filter(
      (r) => r.source_type !== 'REFUND' && !r.voided,
    ),
    [receipts],
  );

  // Visit-level summary tiles. Derived purely from the receipts list,
  // so they tie to the rows below by construction (no second source
  // of truth). `collected` / `refunded` / `net` all route through the
  // shared receiptsApi selectors (Issue-2) — the SAME numbers fed into
  // the reprinted token's Amount Paid / Refunds / Net Paid blocks (FE5,
  // Issue-2 single-source-of-truth rule).
  const visitSummary = useMemo(() => {
    const collected = sumCollectedRupees(receipts);
    const refundedMagnitude = refundLinesFromReceipts(receipts)
      .reduce((sum, l) => sum + l.amount, 0);
    return { collected, refunded: -refundedMagnitude, net: netPaid(receipts) };
  }, [receipts]);

  // GST-DOCNO/A2-d (R-GD-10) — the reprint token's "Receipt No." row must
  // show the SAME string as the original token. `visit.receipt_number` (the
  // bare int denormed onto the visit row, W7 seam) has no printed sibling of
  // its own in this contract, but the matching row in the SAME `receipts`
  // this modal already fetched carries `receipt_number_printed` (§8 C-5) —
  // no extra request. `undefined` (no match, or the field is null) falls
  // through to the bare int in printToken, byte-identical to today.
  //
  // D3 (QA) — the money counter and the credit-note (CN) counter are two
  // independent sequences that both restart at 1 each FY, so a refund's CN
  // number can equal the visit's money receipt int. Matching on
  // `receipt_number` alone can hit the CN row and print its number on a
  // payment token. `visit.receipt_number` is always the MONEY series (never
  // stamped from a refund), so the match must also require the candidate row
  // to carry no `receipt_series` (i.e. not a CN row) to stay unambiguous.
  const visitReceiptNumberPrinted = useMemo(() => {
    if (visit?.receipt_number == null) return undefined;
    const match = receipts.find(
      (r) => r.receipt_number === visit.receipt_number && r.receipt_series == null,
    );
    return match?.receipt_number_printed ?? undefined;
  }, [receipts, visit?.receipt_number]);

  const allSelected = (
    printableReceipts.length > 0
    && printableReceipts.every((r) => selectedIds.has(r.payment_id))
  );

  // ── Receipts tab handlers ──────────────────────────────────────
  const toggleRow = (paymentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) next.delete(paymentId);
      else next.add(paymentId);
      return next;
    });
  };

  const togglePrintAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(printableReceipts.map((r) => r.payment_id)));
  };

  const handlePrint = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || printing) return;
    // Single and multi print share one path: one page per receipt number,
    // concatenated into a single PDF.
    setPrinting(true);
    try {
      const res = await openCombinedReceiptPdf(ids);
      // Close the Receipts modal so the PDF viewer (lower z-index) is visible.
      if (res?.success) onClose();
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintRefundSlip = async (paymentId) => {
    await openRefundSlipPdf(paymentId);
  };

  const handleReprintToken = () => {
    if (!visit) return;
    printToken({
      receipt_number: visit.receipt_number,
      // GST-DOCNO/A2-d — matched from the fetched `receipts` list above.
      receipt_number_printed: visitReceiptNumberPrinted,
      queue_number: visit.queue_number,
      patient: {
        first_name: visit.patient_name?.split(' ')[0] || '',
        last_name: visit.patient_name?.split(' ').slice(1).join(' ') || '',
        patient_id: visit.patient_id,
        // GST-DOCNO/R-GD-6 — real UHID (VisitResponse.patient_uhid); the
        // reprint must show the SAME UHID as the original token.
        uhid: visit.patient_uhid,
      },
      payment_mode: visit.payment_mode,
      visit_time: visit.visit_time,
      // Legacy rows can have an empty visit_date — fall back to created_at
      // so reprint never silently prints today's date (mirrors the
      // registration-time fallback in RegisterVisitForm).
      visit_date: visit.visit_date || visit.created_at,
      // Total collected across every canonical Payment on the visit
      // (consultation + registration + lab + procedure), server-sourced via
      // the SAME `receipts` this modal already fetched — reprint must equal
      // the on-screen "Collected" tile AND the registration-time token
      // (GATE 3 RULING 2 / RC5).
      amount_paid: visitSummary.collected,
      // Issue-1 — same `receipts` this modal already fetched, no new request.
      // amount_paid above already equals sumCollectedRupees(receipts), which
      // equals Σ(line_items) by construction (RC-1), so the printed total
      // always matches whether or not it itemizes.
      line_items: shouldItemizeToken(receipts) ? tokenLineItemsFromReceipts(receipts) : undefined,
      // Issue-2 — same `receipts` this modal already fetched; the shared
      // `refundLinesFromReceipts` selector (also feeds visitSummary above)
      // so the printed Refunds block can never drift from the on-screen
      // "Refunded" tile. `cancelled` traces to visit.status (confirmed
      // field — ManageVisitPage L803 filters on `v.status`).
      refund_lines: refundLinesFromReceipts(receipts),
      cancelled: visit.status === 'CANCELLED',
      // F2 — Department row on the reprinted token (omitted when the caller
      // couldn't resolve a name — see `departmentName` prop doc above).
      department_name: departmentName || undefined,
      clinic_name: branding.name,
      clinic_description: branding.description,
      address: branding.address,
      pincode: branding.pincode,
      phone: branding.phone,
      // Doctor row + Advised-by + assignee (Nurse/Lab) — same as the
      // registration-time token, read from the stored visit.
      ...tokenProviderFields(visit),
    });
  };

  // ── Refund tab handlers ────────────────────────────────────────
  const updateRefundForm = (paymentId, patch) => {
    setRefundForms((prev) => ({
      ...prev,
      [paymentId]: { ...(prev[paymentId] || {}), ...patch },
    }));
  };

  const handleRefundSubmit = (row) => {
    setRefundError(null);
    const form = refundForms[row.payment_id] || {};
    const remaining = Number(row.refundable_remaining_rupees || 0);
    const amt = Number(form.amount || 0);
    if (!amt || amt <= 0) {
      setRefundError('Enter a refund amount greater than 0.');
      return;
    }
    if (amt > remaining) {
      setRefundError(`Refund cannot exceed remaining ₹${remaining.toFixed(2)}.`);
      return;
    }
    const reasonSlug = form.reason || REFUND_REASONS[0].value;
    const reason = reasonSlug === 'other'
      ? (form.reasonText || '').trim()
      : reasonSlug;
    if (!reason) {
      setRefundError('Reason is required.');
      return;
    }
    // F17b — discount reason LOCKS to the original payment's method
    // (row.method, fallback CASH), same rule the disabled select renders;
    // any stale form.method from before the reason was switched is ignored.
    const method = reasonSlug === 'discount'
      ? (row.method || 'CASH')
      : (form.method || 'CASH');
    // F18 — same gate as the row-level hint: full amount (reaches the
    // remaining cap) + reason isn't 'discount'. The confirm dialog is an
    // overlay that hides the row hint while open, so it must repeat the
    // warning here rather than let the user confirm blind.
    const autoCancelOn = row.auto_cancel_on_refund || null;
    const willAutoCancel = Boolean(autoCancelOn) && amt >= remaining && reasonSlug !== 'discount';
    setPendingConfirm({
      paymentId: row.payment_id,
      amount: amt,
      method,
      reason,
      patientName: visit?.patient_name || 'Patient',
      original: Number(row.amount_rupees || 0),
      remainingAfter: remaining - amt,
      description: row.description,
      autoCancelOn,
      willAutoCancel,
      // Stable per logical refund → a double-confirm replays to ONE refund (C-4).
      idempotencyKey: newIdempotencyKey('opd-refund'),
    });
  };

  const handleConfirmRefund = async () => {
    if (!pendingConfirm) return;
    setSubmittingRefundId(pendingConfirm.paymentId);
    const res = await refundPayment(pendingConfirm.paymentId, {
      amount_rupees: String(pendingConfirm.amount),
      reason: pendingConfirm.reason,
      method: pendingConfirm.method,
      idempotency_key: pendingConfirm.idempotencyKey,
    });
    setSubmittingRefundId(null);
    setPendingConfirm(null);
    if (res.success) {
      setLastRefund({
        ...res.data,
        forPatient: visit?.patient_name || 'Patient',
        description: pendingConfirm.description,
        amount: pendingConfirm.amount,
      });
      setRefundForms((prev) => ({ ...prev, [pendingConfirm.paymentId]: {} }));
      // Refresh the list so refunded_remaining + new REFUND row reflect.
      fetchReceipts();
    } else {
      setRefundError(res.error || 'Refund failed.');
    }
  };

  if (!isOpen) return null;

  // ── Style tokens ───────────────────────────────────────────────
  const tokens = isDark
    ? {
      overlayBg: 'rgba(2,6,23,0.6)',
      cardBg: '#0f172a',
      cardBorder: 'rgba(255,255,255,0.10)',
      text: '#e2e8f0',
      muted: '#94a3b8',
      barBg: '#1e293b',
      tabActiveBg: '#0d9488',
      tabActiveText: '#ffffff',
      tabIdleBg: '#0f172a',
      tabIdleText: '#cbd5e1',
      rowBg: '#0f172a',
      rowBorder: 'rgba(255,255,255,0.10)',
      refundRowBg: '#3b0a0a',
      refundRowBorder: 'rgba(239,68,68,0.45)',
      inputBg: '#0b1220',
      inputBorder: 'rgba(255,255,255,0.20)',
      primaryBg: '#0d9488',
      primaryText: '#ffffff',
      secondaryBg: '#1e293b',
      secondaryText: '#e2e8f0',
      successBg: '#064e3b',
      successText: '#d1fae5',
      successBorder: 'rgba(16,185,129,0.55)',
      errorBg: '#3b0a0a',
      errorText: '#fecaca',
    }
    : {
      overlayBg: 'rgba(2,6,23,0.45)',
      cardBg: '#ffffff',
      cardBorder: 'rgba(15,23,42,0.18)',
      text: '#0f172a',
      muted: '#64748b',
      barBg: '#f1f5f9',
      tabActiveBg: '#0d9488',
      tabActiveText: '#ffffff',
      tabIdleBg: '#ffffff',
      tabIdleText: '#334155',
      rowBg: '#ffffff',
      rowBorder: 'rgba(15,23,42,0.16)',
      refundRowBg: '#fef2f2',
      refundRowBorder: 'rgba(239,68,68,0.4)',
      inputBg: '#ffffff',
      inputBorder: 'rgba(15,23,42,0.25)',
      primaryBg: '#0d9488',
      primaryText: '#ffffff',
      secondaryBg: '#ffffff',
      secondaryText: '#0f172a',
      successBg: '#ecfdf5',
      successText: '#065f46',
      successBorder: '#10b981',
      errorBg: '#fef2f2',
      errorText: '#991b1b',
    };

  const inputBase = {
    padding: '0.375rem 0.5rem',
    borderRadius: '0.375rem',
    border: `1px solid ${tokens.inputBorder}`,
    background: tokens.inputBg,
    color: tokens.text,
    fontSize: '0.875rem',
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div
      style={{
        // Phase 4 — zIndex was 100, hidden behind the chart pane
        // (Z_CHART_PANE=900) when the chart was open. Z_DIALOG (1300)
        // keeps the receipts / refund dialog above chart + rail.
        position: 'fixed', inset: 0, zIndex: Z_DIALOG,
        background: tokens.overlayBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: 'min(640px, 100%)',
          maxHeight: '90vh',
          background: tokens.cardBg,
          color: tokens.text,
          border: `1px solid ${tokens.cardBorder}`,
          borderRadius: '0.75rem',
          boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: `1px solid ${tokens.cardBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>
              {mode === 'refund' ? 'Payments' : 'Receipts'}
              {' — '}
              {visit?.patient_name || 'Visit'}
            </div>
            <div style={{ fontSize: '0.75rem', color: tokens.muted, marginTop: '0.125rem' }}>
              Visit #{visit?.receipt_number ?? '—'} · {visit?.visit_date || ''}
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: tokens.secondaryBg, color: tokens.secondaryText,
              border: `1px solid ${tokens.cardBorder}`,
              borderRadius: '0.375rem',
              padding: '0.25rem 0.5rem', fontSize: '0.8rem', cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tab bar removed — Receipts and Refund are reached from
            separate buttons on the visit row. The modal opens in the
            mode it was launched with and stays there. If a user lacks
            REFUND_PAYMENT and somehow lands on mode="refund", the
            refund body below renders an empty-state. */}

        {/* Body */}
        <div style={{ overflow: 'auto', padding: '1rem 1.25rem', flex: 1 }}>
          {loading && (
            <div style={{ color: tokens.muted, fontSize: '0.85rem' }}>
              Loading receipts…
            </div>
          )}
          {error && (
            <div
              style={{
                background: tokens.errorBg, color: tokens.errorText,
                border: `1px solid ${tokens.refundRowBorder}`,
                padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                fontSize: '0.8rem', marginBottom: '0.75rem',
              }}
            >
              {error}
            </div>
          )}

          {/* ─── Receipts tab ───────────────────────────────────────── */}
          {!loading && activeTab === 'receipts' && (
            <div>
              {printableReceipts.length === 0 ? (
                <div style={{ color: tokens.muted, fontSize: '0.85rem' }} data-testid="visit-receipts-empty">
                  {!error && isLabOrderId
                    ? 'Not yet settled — no receipt to print.'
                    : 'No printable receipts attached to this visit yet.'}
                </div>
              ) : (
                <>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: tokens.barBg,
                    border: `1px solid ${tokens.rowBorder}`,
                    borderRadius: '0.5rem',
                    cursor: 'pointer', marginBottom: '0.5rem',
                  }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={togglePrintAll}
                    />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      Print all ({printableReceipts.length})
                    </span>
                  </label>
                  {printableReceipts.map((r) => {
                    const b = badge(r.source_type);
                    const checked = selectedIds.has(r.payment_id);
                    return (
                      <label
                        key={r.payment_id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          background: tokens.rowBg,
                          border: `1px solid ${tokens.rowBorder}`,
                          borderRadius: '0.5rem',
                          marginBottom: '0.375rem',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRow(r.payment_id)}
                        />
                        <span style={{ fontSize: '0.85rem' }}>{b.icon}</span>
                        <span style={{ fontSize: '0.8rem', flex: 1 }}>
                          <span style={{ fontWeight: 600 }}>
                            #{r.receipt_number ?? '—'}
                          </span>
                          {'  '}
                          {r.description}
                          {Number(r.refunded_so_far_rupees || 0) > 0 && (
                            <span style={{
                              marginLeft: '0.5rem',
                              fontSize: '0.7rem',
                              color: '#dc2626',
                            }}>
                              (refunded ₹{fmtMoney(r.refunded_so_far_rupees)})
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          ₹{fmtMoney(r.amount_rupees)}
                        </span>
                      </label>
                    );
                  })}
                </>
              )}

              {/* Refund history (audit only, not selectable) */}
              {receipts.some((r) => r.source_type === 'REFUND') && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{
                    fontSize: '0.75rem', fontWeight: 600,
                    color: tokens.muted, marginBottom: '0.25rem',
                  }}>
                    Refund history (audit)
                  </div>
                  {receipts.filter((r) => r.source_type === 'REFUND').map((r) => (
                    <div
                      key={r.payment_id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: tokens.refundRowBg,
                        border: `1px solid ${tokens.refundRowBorder}`,
                        borderRadius: '0.5rem',
                        marginBottom: '0.375rem',
                      }}
                    >
                      <span>↩️</span>
                      <span style={{ fontSize: '0.78rem', flex: 1, color: tokens.text }}>
                        <span style={{ fontWeight: 600 }}>{refundReceiptLabel(r)}</span>
                        {'  '}
                        Refund
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626' }}>
                        ₹{fmtMoney(r.net_amount_rupees)}
                      </span>
                      <button
                        onClick={() => handlePrintRefundSlip(r.payment_id)}
                        title="Reprint refund slip"
                        style={{
                          background: tokens.secondaryBg,
                          color: tokens.secondaryText,
                          border: `1px solid ${tokens.inputBorder}`,
                          borderRadius: '0.375rem',
                          padding: '0.2rem 0.4rem', fontSize: '0.75rem',
                          cursor: 'pointer',
                        }}
                      >
                        🖨️
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '0.75rem' }}>
                <button
                  onClick={handleReprintToken}
                  style={{
                    background: 'transparent',
                    color: tokens.muted,
                    border: 'none',
                    fontSize: '0.75rem',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: '0.25rem 0',
                  }}
                >
                  or reprint the queue token (80mm)
                </button>
              </div>
            </div>
          )}

          {/* ─── Refund tab ─────────────────────────────────────────── */}
          {!loading && activeTab === 'refund' && (
            <div>
              {lastRefund && (
                <div
                  style={{
                    background: tokens.successBg, color: tokens.successText,
                    border: `1px solid ${tokens.successBorder}`,
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    marginBottom: '0.75rem',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    ✓ Refunded ₹{fmtMoney(lastRefund.amount)} to {lastRefund.forPatient}
                  </div>
                  <div style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                    Receipt {refundReceiptLabel(lastRefund)} issued.
                  </div>
                  <button
                    onClick={() => handlePrintRefundSlip(lastRefund.payment_id)}
                    style={{
                      background: tokens.primaryBg, color: tokens.primaryText,
                      border: 'none', borderRadius: '0.375rem',
                      padding: '0.375rem 0.625rem', fontSize: '0.8rem',
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    🖨️  Print refund slip
                  </button>
                </div>
              )}

              {refundError && (
                <div
                  style={{
                    background: tokens.errorBg, color: tokens.errorText,
                    border: `1px solid ${tokens.refundRowBorder}`,
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    fontSize: '0.8rem', marginBottom: '0.5rem',
                  }}
                >
                  {refundError}
                </div>
              )}

              {/* Visit-level summary tiles — Collected / Refunded / Net.
                  Computed from `receipts` so they tie 1:1 to the rows
                  below. Same palette tokens as DailyCollectionSummary. */}
              <PaymentsSummaryTiles
                collected={visitSummary.collected}
                refunded={visitSummary.refunded}
                net={visitSummary.net}
                isDark={isDark}
              />

              {/* Owner ruling — no refund on an IPD lab charge. The server
                  already refuses the write and every row's `refund_eligible`
                  already comes back false for one (single-source, from
                  `refund_policy.compute_refund_eligibility` reusing
                  `lab_settlement.is_ipd_lab`) — this banner is a second,
                  up-front mirror so staff don't have to expand a row to
                  learn that. COPY: PROVISIONAL, NEEDS-PO. */}
              {isLabOrderId && (
                <div
                  style={{
                    color: tokens.muted, fontSize: '0.8rem',
                    padding: '0.625rem 0.75rem',
                    background: tokens.barBg,
                    border: `1px solid ${tokens.rowBorder}`,
                    borderRadius: '0.5rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  Refunds aren't available for IPD lab charges — this is billed
                  through the admission's invoice.
                </div>
              )}

              {paymentRows.length === 0 ? (
                <div
                  style={{
                    color: tokens.muted, fontSize: '0.85rem',
                    padding: '0.75rem',
                    background: tokens.barBg, borderRadius: '0.5rem',
                    marginTop: '0.5rem',
                  }}
                >
                  No payments collected for this visit yet.
                </div>
              ) : (
                <>
                  <div style={{
                    fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: tokens.muted,
                    margin: '0.5rem 0 0.25rem',
                  }}>
                    Receipts
                  </div>
                  {paymentRows.map((r) => {
                    const remaining = Number(r.refundable_remaining_rupees || 0);
                    const refundedSoFar = Number(r.refunded_so_far_rupees || 0);
                    const amount = Number(r.amount_rupees || 0);
                    const isFullyRefunded = remaining === 0 && refundedSoFar > 0;
                    const isPartiallyRefunded = remaining > 0 && refundedSoFar > 0;
                    const hasBalanceLeft = remaining > 0;
                    const isNoBalance = amount === 0;
                    // Lifecycle eligibility from the backend — gates the
                    // refund action by visit/sitting state, not by money math.
                    const lifecycleEligible = r.refund_eligible !== false;
                    const lifecycleBlockReason = r.refund_block_reason || null;
                    const autoCancelOn = r.auto_cancel_on_refund || null;
                    // Form renders only when BOTH gates pass:
                    //  - lifecycle says yes (source not in-progress / not completed)
                    //  - amount-math says yes (refundable_remaining > 0)
                    const isRefundable = hasBalanceLeft && lifecycleEligible;

                    // Badge precedence:
                    //  - Lifecycle block wins (Cancel required / Cannot refund)
                    //  - then balance state (Fully / Partial / No balance)
                    //  - else Refundable
                    let badge;
                    if (hasBalanceLeft && !lifecycleEligible) {
                      // Blocked by lifecycle — choose colour by reason
                      // ("in progress" → amber, "completed" → gray, etc.).
                      const reason = (lifecycleBlockReason || '').toLowerCase();
                      if (reason.includes('in progress')) {
                        badge = { label: 'Cancel required', bg: isDark ? '#78350f' : '#fef3c7', text: isDark ? '#fde68a' : '#92400e' };
                      } else if (reason.includes('completed')) {
                        badge = { label: 'Cannot refund', bg: isDark ? '#374151' : '#e5e7eb', text: isDark ? '#d1d5db' : '#374151' };
                      } else {
                        badge = { label: 'Refund blocked', bg: isDark ? '#374151' : '#e5e7eb', text: isDark ? '#d1d5db' : '#374151' };
                      }
                    } else if (isFullyRefunded) {
                      badge = { label: 'Fully refunded', bg: isDark ? '#374151' : '#e5e7eb', text: isDark ? '#d1d5db' : '#374151' };
                    } else if (isPartiallyRefunded) {
                      badge = { label: 'Partially refunded', bg: isDark ? '#78350f' : '#fef3c7', text: isDark ? '#fde68a' : '#92400e' };
                    } else if (isNoBalance) {
                      badge = { label: 'No balance', bg: isDark ? '#374151' : '#e5e7eb', text: isDark ? '#d1d5db' : '#374151' };
                    } else {
                      badge = { label: 'Refundable', bg: isDark ? '#134e4a' : '#ccfbf1', text: isDark ? '#5eead4' : '#0f766e' };
                    }

                    const form = refundForms[r.payment_id] || {};
                    const submittingThis = submittingRefundId === r.payment_id;
                    const reasonSlug = form.reason || REFUND_REASONS[0].value;
                    // Refund method defaults to CASH regardless of the
                    // parent's method — receptionists physically hand
                    // cash back at the counter as the default flow.
                    // For UPI/Card refunds (gateway reversal) they
                    // explicitly select it from the dropdown.
                    // F17b exception — reason "Discount" is a ledger
                    // adjustment against the SAME rail the patient paid
                    // on, not a counter hand-back, so the method LOCKS to
                    // the original payment's method (row.method, fallback
                    // CASH) and the dropdown disables. Picking any other
                    // reason re-enables it with the normal CASH default.
                    const isDiscountReason = reasonSlug === 'discount';
                    const methodValue = isDiscountReason
                      ? (r.method || 'CASH')
                      : (form.method || 'CASH');
                    // F18 — mirrors the server rule (BE-F18): auto-cancel
                    // only fires when the refund is FULL (entered amount
                    // reaches the remaining cap) and reason isn't
                    // 'discount'. Advisory only — server stays sole
                    // enforcer.
                    const enteredAmount = Number(form.amount) || 0;
                    const isFullAmountEntered = enteredAmount > 0 && enteredAmount >= remaining;
                    const willAutoCancel = Boolean(autoCancelOn) && isFullAmountEntered && !isDiscountReason;
                    return (
                      <div
                        key={r.payment_id}
                        style={{
                          background: tokens.rowBg,
                          border: `1px solid ${tokens.rowBorder}`,
                          borderRadius: '0.5rem',
                          padding: '0.625rem 0.75rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <div style={{
                          display: 'flex', alignItems: 'baseline',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                          marginBottom: '0.25rem',
                        }}>
                          <div style={{ fontSize: '0.85rem' }}>
                            <span style={{ fontWeight: 600 }}>
                              #{r.receipt_number ?? '—'}
                            </span>
                            {'  '}
                            {r.description}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                              ₹{fmtMoney(amount)}
                            </span>
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 600,
                              padding: '0.125rem 0.4rem',
                              borderRadius: '0.625rem',
                              background: badge.bg, color: badge.text,
                              whiteSpace: 'nowrap',
                            }}>
                              {badge.label}
                            </span>
                          </div>
                        </div>

                        {/* Sub-line: refunded-so-far for partial + fully refunded rows. */}
                        {(isPartiallyRefunded || isFullyRefunded) && (
                          <div style={{
                            fontSize: '0.72rem', color: tokens.muted,
                            marginBottom: isRefundable ? '0.4rem' : 0,
                          }}>
                            {isFullyRefunded
                              ? `Refunded ₹${fmtMoney(refundedSoFar)} — no balance left.`
                              : `Refunded so far ₹${fmtMoney(refundedSoFar)} · Refundable ₹${fmtMoney(remaining)}`}
                          </div>
                        )}

                        {/* Lifecycle block reason — surfaces when the
                            row has balance left but the source visit /
                            sitting is in a state that disallows refund. */}
                        {hasBalanceLeft && !lifecycleEligible && lifecycleBlockReason && (
                          <div style={{
                            fontSize: '0.72rem', color: tokens.muted,
                            fontStyle: 'italic',
                          }}>
                            {lifecycleBlockReason}
                          </div>
                        )}

                        {/* Auto-cancel hint — F18: dynamic. Only shown
                            once the currently-entered amount reaches the
                            remaining cap (a full refund) and the reason
                            isn't 'discount' — the same gate the server
                            uses to decide whether it actually cancels.
                            Partial amounts and discount refunds never
                            cancel, so they show no warning. */}
                        {isRefundable && willAutoCancel && (
                          <div style={{
                            fontSize: '0.7rem', color: tokens.muted,
                            marginBottom: '0.4rem',
                          }}>
                            {autoCancelWarningText(autoCancelOn)}
                          </div>
                        )}

                        {/* Inline refund form — only on refundable / partial rows. */}
                        {isRefundable && (
                          <>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1.4fr 1fr',
                              gap: '0.5rem',
                              marginBottom: '0.4rem',
                            }}>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                max={remaining}
                                placeholder={`₹ up to ${remaining}`}
                                value={form.amount || ''}
                                onChange={(e) => updateRefundForm(r.payment_id, {
                                  amount: e.target.value,
                                })}
                                style={inputBase}
                              />
                              <select
                                value={reasonSlug}
                                onChange={(e) => updateRefundForm(r.payment_id, {
                                  reason: e.target.value,
                                })}
                                style={inputBase}
                              >
                                {REFUND_REASONS.map((rr) => (
                                  <option key={rr.value} value={rr.value}>{rr.label}</option>
                                ))}
                              </select>
                              <select
                                value={methodValue}
                                disabled={isDiscountReason}
                                onChange={(e) => updateRefundForm(r.payment_id, {
                                  method: e.target.value,
                                })}
                                style={isDiscountReason
                                  ? { ...inputBase, background: tokens.barBg, color: tokens.muted, cursor: 'not-allowed' }
                                  : inputBase}
                              >
                                {REFUND_METHODS.map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </div>

                            {isDiscountReason && (
                              <div style={{
                                fontSize: '0.72rem', color: tokens.muted,
                                marginTop: '-0.15rem', marginBottom: '0.4rem',
                              }}>
                                Discount refunds go back via the original payment method.
                              </div>
                            )}

                            {reasonSlug === 'other' && (
                              <input
                                type="text"
                                placeholder="Specify reason"
                                value={form.reasonText || ''}
                                onChange={(e) => updateRefundForm(r.payment_id, {
                                  reasonText: e.target.value,
                                })}
                                style={{ ...inputBase, width: '100%', marginBottom: '0.4rem' }}
                              />
                            )}

                            {Number(form.amount) > 0 && (
                              <div style={{
                                fontSize: '0.75rem', color: tokens.muted,
                                marginBottom: '0.4rem',
                              }}>
                                Refunding ₹{Number(form.amount).toFixed(2)} of
                                {' '}₹{fmtMoney(amount)} to
                                {' '}{visit?.patient_name || 'patient'} via {methodValue}
                              </div>
                            )}

                            <div style={{ textAlign: 'right' }}>
                              <button
                                onClick={() => handleRefundSubmit(r)}
                                disabled={submittingThis}
                                style={{
                                  background: tokens.primaryBg, color: tokens.primaryText,
                                  border: 'none', borderRadius: '0.375rem',
                                  padding: '0.375rem 0.875rem', fontSize: '0.8rem',
                                  fontWeight: 600, cursor: 'pointer',
                                  opacity: submittingThis ? 0.6 : 1,
                                }}
                              >
                                {submittingThis ? 'Submitting…' : 'Submit refund'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* Refund history — same audit strip the Receipts tab
                  shows, repeated here so a payment-management session
                  doesn't need a tab-switch to see what's been reversed. */}
              {receipts.some((r) => r.source_type === 'REFUND') && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{
                    fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: tokens.muted,
                    marginBottom: '0.25rem',
                  }}>
                    Refund history (audit)
                  </div>
                  {receipts.filter((r) => r.source_type === 'REFUND').map((r) => (
                    <div
                      key={r.payment_id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: tokens.refundRowBg,
                        border: `1px solid ${tokens.refundRowBorder}`,
                        borderRadius: '0.5rem',
                        marginBottom: '0.375rem',
                      }}
                    >
                      <span>↩️</span>
                      <span style={{ fontSize: '0.78rem', flex: 1, color: tokens.text }}>
                        <span style={{ fontWeight: 600 }}>{refundReceiptLabel(r)}</span>
                        {'  '}Refund
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626' }}>
                        ₹{fmtMoney(r.net_amount_rupees)}
                      </span>
                      <button
                        onClick={() => handlePrintRefundSlip(r.payment_id)}
                        title="Reprint refund slip"
                        style={{
                          background: tokens.secondaryBg,
                          color: tokens.secondaryText,
                          border: `1px solid ${tokens.inputBorder}`,
                          borderRadius: '0.375rem',
                          padding: '0.2rem 0.4rem', fontSize: '0.75rem',
                          cursor: 'pointer',
                        }}
                      >
                        🖨️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer (Receipts tab only) */}
        {activeTab === 'receipts' && (
          <div
            style={{
              padding: '0.75rem 1.25rem',
              borderTop: `1px solid ${tokens.cardBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              gap: '0.5rem',
              background: tokens.barBg,
            }}
          >
            <button
              onClick={onClose}
              style={{
                background: tokens.secondaryBg, color: tokens.secondaryText,
                border: `1px solid ${tokens.inputBorder}`,
                borderRadius: '0.375rem',
                padding: '0.5rem 0.875rem', fontSize: '0.85rem',
                fontWeight: 500, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handlePrint}
              disabled={selectedIds.size === 0 || printing}
              style={{
                background: tokens.primaryBg, color: tokens.primaryText,
                border: 'none',
                borderRadius: '0.375rem',
                padding: '0.5rem 0.875rem', fontSize: '0.85rem',
                fontWeight: 600, cursor: (selectedIds.size === 0 || printing) ? 'default' : 'pointer',
                opacity: (selectedIds.size === 0 || printing) ? 0.6 : 1,
              }}
            >
              {printing ? 'Generating…' : `Print selected (${selectedIds.size})`}
            </button>
          </div>
        )}

        {/* Refund confirmation dialog (inline overlay over the modal) */}
        {pendingConfirm && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 'min(380px, 92%)',
                background: tokens.cardBg, color: tokens.text,
                border: `1px solid ${tokens.cardBorder}`,
                borderRadius: '0.5rem',
                padding: '1rem',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Confirm refund
              </div>
              <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem', lineHeight: 1.4 }}>
                Refund <strong>₹{pendingConfirm.amount.toFixed(2)}</strong>
                {' '}to {pendingConfirm.patientName} via {pendingConfirm.method}?
                <br />
                <span style={{ color: tokens.muted, fontSize: '0.78rem' }}>
                  {pendingConfirm.remainingAfter > 0
                    ? `After this refund, ₹${pendingConfirm.remainingAfter.toFixed(2)} will remain refundable on the original ${pendingConfirm.description} (₹${pendingConfirm.original.toFixed(2)} collected).`
                    : `This will fully refund the remaining balance on the original ${pendingConfirm.description} (₹${pendingConfirm.original.toFixed(2)} collected).`}
                  {' '}This action is irreversible.
                  {/* F18 — the row's auto-cancel hint is hidden behind this
                      overlay while it's open, so repeat it here (same
                      gate, same copy via autoCancelWarningText) rather
                      than let the user confirm blind to the cancel. */}
                  {pendingConfirm.willAutoCancel && (
                    <>
                      <br />
                      {autoCancelWarningText(pendingConfirm.autoCancelOn)}
                    </>
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  onClick={() => setPendingConfirm(null)}
                  style={{
                    background: tokens.secondaryBg, color: tokens.secondaryText,
                    border: `1px solid ${tokens.inputBorder}`,
                    borderRadius: '0.375rem',
                    padding: '0.4rem 0.75rem', fontSize: '0.8rem',
                    fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRefund}
                  style={{
                    background: '#dc2626', color: '#ffffff',
                    border: 'none', borderRadius: '0.375rem',
                    padding: '0.4rem 0.75rem', fontSize: '0.8rem',
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Confirm refund
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
